import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { Award, Calendar, IdCard, Mail, Phone, MapPin, Tag, Sparkles, ArrowRight, Globe, Clock, Trophy, Search } from "lucide-react";
import { api } from "../../lib/api";

const PRIMARY = "#6B2D5C";
const SECONDARY = "#ec4899";
const ACCENT = "#06b6d4";

// Token customer (différent du token admin) — l'intercepteur api.js ne l'ajoute pas automatiquement
function customerAuthHeaders() {
  const t = localStorage.getItem("msc_customer_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

// =============================================================================
// CARTE DIGITALE — 3D animée (rotation continue + tilt souris + hologramme shimmer)
// L'animation continue rend une simple capture d'écran inutile (anti-usurpation).
// =============================================================================
function DigitalLicenseCard({ data, compact = false }) {
  const cardRef = React.useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50 });
  const [hovering, setHovering] = useState(false);

  if (!data) return null;
  const fullName = `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Licencié";
  const seasonLabel = data.license_season || "—";

  const handleMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const px = (x / r.width) * 100;
    const py = (y / r.height) * 100;
    // Tilt jusqu'à ±10° selon position
    const ry = ((px - 50) / 50) * 12;
    const rx = -((py - 50) / 50) * 10;
    setTilt({ rx, ry, mx: px, my: py });
  };
  const handleLeave = () => {
    setHovering(false);
    setTilt({ rx: 0, ry: 0, mx: 50, my: 50 });
  };

  return (
    <div
      className="mx-auto max-w-2xl"
      style={{ perspective: "1200px" }}
      data-testid="digital-license-card-wrapper"
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMove}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={handleLeave}
        animate={hovering ? {
          rotateY: tilt.ry,
          rotateX: tilt.rx,
        } : {
          rotateY: [-4, 4, -4],
          rotateX: [2, -2, 2],
        }}
        transition={hovering ? {
          type: "spring", stiffness: 200, damping: 18,
        } : {
          duration: 6, repeat: Infinity, ease: "easeInOut",
        }}
        className="relative rounded-3xl overflow-hidden shadow-2xl mx-auto"
        style={{
          background: `linear-gradient(160deg, ${PRIMARY} 0%, #4a1f40 50%, #1a0a18 100%)`,
          aspectRatio: "1 / 1.586", // PORTRAIT (carte verticale)
          width: "min(100%, 420px)",
          border: "2px solid #e9c878",
          boxShadow: "0 25px 50px -12px rgba(107,45,92,0.6), inset 0 0 0 1px rgba(233,200,120,0.3)",
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        data-testid="digital-license-card"
      >
        {/* HOLOGRAMME — bande lumineuse diagonale qui glisse en boucle */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background:
              "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 45%, rgba(233,200,120,0.32) 50%, rgba(255,255,255,0.18) 55%, transparent 70%)",
            backgroundSize: "200% 100%",
            animation: "iphbHoloShimmer 3.6s linear infinite",
          }}
        />

        {/* SPOTLIGHT au curseur */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none transition-opacity duration-500"
          style={{
            opacity: hovering ? 0.7 : 0,
            background: `radial-gradient(circle at ${tilt.mx}% ${tilt.my}%, rgba(233,200,120,0.35), transparent 45%)`,
            mixBlendMode: "screen",
          }}
        />

        {/* Halos décoratifs animés (pulse synchronisé) */}
        <motion.div
          animate={{ opacity: [0.15, 0.3, 0.15], scale: [1, 1.1, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-24 -right-20 w-72 h-72 rounded-full blur-2xl"
          style={{ background: SECONDARY, transform: `translateZ(20px)` }}/>
        <motion.div
          animate={{ opacity: [0.1, 0.22, 0.1], scale: [1, 1.15, 1] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full blur-2xl"
          style={{ background: ACCENT, transform: `translateZ(15px)` }}/>

        {/* SPEED LINES — rayures diagonales or */}
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage: `repeating-linear-gradient(115deg, transparent 0px, transparent 22px, rgba(233,200,120,0.4) 22px, rgba(233,200,120,0.4) 24px)`,
          }}/>

        {/* Pattern hexagonal subtil (grain moderne sport) */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(233,200,120,0.18) 1px, transparent 1.5px)`,
          backgroundSize: "16px 16px",
        }}/>

        {/* TAUREAU IPHB — grand, à droite, semi-transparent (rappel identité club) */}
        {data.club_logo && (
          <motion.div
            animate={{ rotate: [-2, 2, -2], scale: [1, 1.04, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-10 top-1/2 -translate-y-1/2 pointer-events-none select-none"
            style={{ width: "75%", transform: "translateY(-50%) translateZ(5px)" }}>
            <img src={data.club_logo} alt="" className="w-full h-auto"
              style={{ opacity: 0.16, filter: "drop-shadow(0 0 24px rgba(233,200,120,0.35))" }}/>
          </motion.div>
        )}

        {/* Taureau plus petit, à gauche (deuxième rappel pour effet "graphique") */}
        {data.club_logo && (
          <div className="absolute -left-12 -top-6 pointer-events-none select-none"
            style={{ width: "55%", transform: "translateZ(8px) rotate(-15deg)" }}>
            <img src={data.club_logo} alt="" className="w-full h-auto"
              style={{ opacity: 0.07, filter: "drop-shadow(0 0 16px rgba(255,255,255,0.2))" }}/>
          </div>
        )}

        {/* Petites particules dorées flottantes */}
        {[
          { left: "12%", top: "22%", size: 3, dur: 3.2 },
          { left: "78%", top: "18%", size: 2, dur: 2.8, delay: 0.5 },
          { left: "22%", top: "70%", size: 2.5, dur: 4.2, delay: 1 },
          { left: "85%", top: "62%", size: 3, dur: 3.6, delay: 0.8 },
          { left: "55%", top: "82%", size: 2, dur: 2.6, delay: 1.5 },
        ].map((p, i) => (
          <motion.div key={i}
            animate={{
              opacity: [0.2, 0.9, 0.2],
              scale: [0.8, 1.3, 0.8],
            }}
            transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay || 0 }}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: p.left, top: p.top,
              width: p.size, height: p.size,
              background: "#e9c878",
              boxShadow: "0 0 8px rgba(233,200,120,0.8)",
            }}/>
        ))}

        {/* Bandeau supérieur : LOGO + LICENCE OFFICIELLE */}
        <div className="absolute top-0 left-0 right-0 px-4 pt-3 pb-2.5 flex items-center justify-between border-b z-10"
          style={{ borderColor: "rgba(233,200,120,0.25)", transform: "translateZ(15px)" }}>
          <div className="flex items-center gap-2">
            {data.club_logo && (
              <img src={data.club_logo} alt={data.club_name || "IPHB"} className="w-20 h-20 object-contain drop-shadow-lg"/>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-black" style={{ color: "#e9c878" }}>
                {data.club_name || "Istres Provence HB"}
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] font-bold" style={{ color: "#e9c878", opacity: 0.7 }}>
                Licence Officielle
              </div>
            </div>
          </div>
          <span className="inline-block w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: "#e9c878" }}/>
        </div>

        {/* CONTENU PORTRAIT — vertical */}
        <div className="relative h-full flex flex-col items-center justify-between pt-20 pb-5 px-5 z-10"
          style={{ transform: "translateZ(30px)" }}>

          {/* Photo licencié — grand format au centre haut, en ROND */}
          <div className="flex flex-col items-center gap-2">
            <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden flex-shrink-0 shadow-2xl"
              style={{ border: "3px solid #e9c878", background: "rgba(255,255,255,0.05)", transform: "translateZ(20px)" }}>
              {data.license_photo ? (
                <img src={data.license_photo} alt={fullName} className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-3xl font-black">
                  {(data.first_name?.[0] || "?").toUpperCase()}{(data.last_name?.[0] || "").toUpperCase()}
                </div>
              )}
            </div>

            {/* Nom + pastille licencié */}
            <div className="text-center mt-2">
              <div className="text-white font-black uppercase truncate max-w-[300px]" style={{
                fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif",
                fontSize: "clamp(1.5rem, 5.8vw, 2rem)",
                lineHeight: 1.05,
                letterSpacing: "0.05em",
                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              }}>
                {fullName}
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
                style={{ background: "rgba(233,200,120,0.15)", color: "#e9c878", border: "1px solid rgba(233,200,120,0.45)" }}>
                <Award size={11}/> Licencié actif · {seasonLabel}
              </div>
            </div>
          </div>

          {/* Infos en grille — N° + Catégorie + Adhésion */}
          <div className="w-full grid grid-cols-3 gap-2 py-3 border-y my-2"
            style={{ borderColor: "rgba(233,200,120,0.2)" }}>
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>N° Licence</div>
              <div className="font-black text-[14px] tabular-nums truncate mt-1"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", color: "#e9c878" }}>
                {data.license_number || "—"}
              </div>
            </div>
            <div className="text-center border-x" style={{ borderColor: "rgba(233,200,120,0.2)" }}>
              <div className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>Catégorie</div>
              <div className="text-white font-black text-[14px] truncate mt-1">{data.license_category || "—"}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>Membre depuis</div>
              <div className="text-white font-black text-[14px] mt-1">
                {data.member_since ? new Date(data.member_since).getFullYear() : "—"}
              </div>
            </div>
          </div>

          {/* Logo FFHB officiel — remplace le QR code */}
          <div className="flex flex-col items-center gap-1.5"
            style={{ transform: "translateZ(25px)" }}>
            <div className="bg-white p-3 rounded-xl shadow-xl flex items-center justify-center"
              style={{ border: "1.5px solid rgba(233,200,120,0.5)" }}
              data-testid="license-ffhb-logo">
              <img src="/uploads/logo-ffhb-real.png" alt="Fédération Française de Handball"
                className="w-24 h-24 object-contain"
                onError={(e) => { e.currentTarget.src = "/uploads/logo-ffhb.svg"; }}/>
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-center font-bold" style={{ color: "#e9c878", opacity: 0.95 }}>
              Affilié FFHandball
            </div>
          </div>

          {/* SCEAU CIRCULAIRE "VALIDE" — coin bas droit (rotation lente continue) */}
          <motion.div
            animate={{ rotate: [-12, -8, -12] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-4 right-4 w-24 h-24 rounded-full flex items-center justify-center pointer-events-none"
            style={{
              border: "3px dashed rgba(233,200,120,0.6)",
              transform: "translateZ(35px)",
              background: "rgba(0,0,0,0.25)",
              backdropFilter: "blur(4px)",
            }}>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.15em] font-black" style={{ color: "#e9c878", opacity: 0.9 }}>
                Valide
              </div>
              <div className="font-black tabular-nums leading-none mt-0.5" style={{
                color: "#e9c878",
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.95rem",
                letterSpacing: "0.02em",
                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              }}>
                {seasonLabel.split("-").pop() || "26"}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Keyframes injectées globalement (une seule fois) */}
      <style>{`
        @keyframes iphbHoloShimmer {
          0%   { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// CARD OFFRE PRÉFÉRENTIELLE
// =============================================================================
function LicenseeOfferCard({ offer }) {
  const p = offer.partner || {};
  const [revealed, setRevealed] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white shadow-lg overflow-hidden border border-slate-100 flex flex-col"
      data-testid={`licensee-offer-${offer.id}`}
    >
      {/* Bandeau remise */}
      <div className="relative px-4 py-3" style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
        <div className="flex items-start gap-3">
          {p.logo_url ? (
            <div className="w-12 h-12 rounded-lg bg-white p-1.5 shadow flex items-center justify-center flex-shrink-0">
              <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain"/>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0 text-white font-black text-base">
              {(p.name || "?").charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0 pr-2">
            <div className="text-[9px] uppercase tracking-widest text-white/70 font-bold">{p.name || "Partenaire IPHB"}</div>
            <div className="font-black text-white text-sm leading-tight line-clamp-2">{offer.title}</div>
          </div>
          {offer.discount_label && (
            <div className="bg-white/95 rounded-full px-2.5 py-1 flex-shrink-0">
              <span className="font-black text-xs whitespace-nowrap" style={{ color: PRIMARY, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}>
                {offer.discount_label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="p-4 flex-1 flex flex-col">
        {offer.description && (
          <p className="text-[12px] text-slate-600 leading-relaxed line-clamp-3 mb-3">{offer.description}</p>
        )}

        {/* Code promo masqué */}
        {offer.code && (
          <div className="mb-3">
            <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1">Code à présenter</div>
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 border-dashed transition"
              style={{ borderColor: `${SECONDARY}50`, background: `${SECONDARY}08` }}
              data-testid={`offer-reveal-${offer.id}`}
            >
              <span className="font-black text-sm tabular-nums" style={{ color: PRIMARY, letterSpacing: "0.1em" }}>
                {revealed ? offer.code : "•••• •••• ••••"}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-black" style={{ color: SECONDARY }}>
                {revealed ? "Visible" : "Révéler"}
              </span>
            </button>
          </div>
        )}

        {/* Validité */}
        {offer.valid_until && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-3">
            <Calendar size={11}/> Valable jusqu'au {new Date(offer.valid_until).toLocaleDateString("fr-FR")}
          </div>
        )}

        {/* Coordonnées partenaire */}
        <div className="mt-auto pt-3 border-t border-slate-100 space-y-1 text-[11px]">
          {p.contact_name && (
            <div className="font-bold text-slate-700">{p.contact_name}</div>
          )}
          {p.contact_email && (
            <a href={`mailto:${p.contact_email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-pink-600 transition truncate"
              data-testid={`offer-email-${offer.id}`}>
              <Mail size={11} className="shrink-0"/> {p.contact_email}
            </a>
          )}
          {p.contact_phone && (
            <a href={`tel:${(p.contact_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-slate-600 hover:text-pink-600 transition"
              data-testid={`offer-phone-${offer.id}`}>
              <Phone size={11} className="shrink-0"/> {p.contact_phone}
            </a>
          )}
          {p.address && (
            <div className="flex items-start gap-1.5 text-slate-500"><MapPin size={11} className="mt-0.5 shrink-0"/> {p.address}</div>
          )}
          {p.website_url && (
            <a href={p.website_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-pink-600 hover:gap-1.5 transition font-bold uppercase tracking-wider text-[10px]">
              <Globe size={11}/> Visiter le site <ArrowRight size={11}/>
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// =============================================================================
// PLANNING DE LA SEMAINE — vue calendrier compacte (entraînements + matchs)
// =============================================================================
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function ScheduleSection({ schedule }) {
  if (!schedule) return null;
  const sessions = schedule.sessions || [];
  // Groupe par jour
  const byDay = {};
  for (const s of sessions) {
    const d = s.weekday;
    if (!byDay[d]) byDay[d] = [];
    byDay[d].push(s);
  }
  return (
    <section data-testid="licensee-schedule">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={18} style={{ color: ACCENT }}/>
          <h2 className="font-black uppercase tracking-widest text-sm" style={{ color: PRIMARY }}>
            Mon planning · {schedule.category || "—"}
          </h2>
        </div>
        <span className="text-xs text-slate-500 font-bold">{sessions.length} séance{sessions.length > 1 ? "s" : ""}/semaine</span>
      </div>
      {sessions.length === 0 ? (
        <div className="rounded-xl bg-slate-50 p-8 text-center text-slate-500 text-sm">
          <Clock size={28} className="mx-auto mb-2 opacity-30"/>
          Aucune séance programmée pour le moment. Le coach mettra à jour le planning bientôt.
        </div>
      ) : (
        <div className="rounded-2xl bg-white shadow-md overflow-hidden border border-slate-100">
          <div className="grid grid-cols-7 divide-x divide-slate-100">
            {WEEKDAYS.map((d, i) => {
              const items = byDay[i] || [];
              const isToday = new Date().getDay() === ((i + 1) % 7);
              return (
                <div key={d} className={`min-h-[110px] flex flex-col ${isToday ? "" : ""}`}
                  style={isToday ? { background: `${ACCENT}08` } : {}}>
                  <div className={`px-2 py-2 text-[10px] uppercase tracking-widest font-black text-center ${isToday ? "text-cyan-700" : "text-slate-500"}`}
                    style={isToday ? { borderBottom: `2px solid ${ACCENT}` } : { borderBottom: "1px solid #e5e7eb" }}>
                    {d}
                    {isToday && <div className="text-[8px] mt-0.5" style={{ color: ACCENT }}>· Aujourd'hui</div>}
                  </div>
                  <div className="flex-1 p-1.5 space-y-1.5">
                    {items.map((s) => (
                      <div key={s.id} className="rounded-lg p-1.5 text-[10px] leading-tight"
                        style={{ background: `${s.color}15`, border: `1px solid ${s.color}40` }}
                        title={s.notes}
                        data-testid={`schedule-${s.id}`}>
                        <div className="flex items-center gap-1 font-black uppercase tracking-wider"
                          style={{ color: s.color }}>
                          {s.kind === "match" ? <Trophy size={9}/> : <Clock size={9}/>}
                          {s.start_time}
                        </div>
                        <div className="font-bold text-[10px] text-slate-800 line-clamp-2 mt-0.5">{s.title || (s.kind === "match" ? "Match" : "Entraînement")}</div>
                        {s.location && (
                          <div className="text-[9px] text-slate-500 line-clamp-1 mt-0.5">{s.location}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-xs text-slate-500 mt-2 italic">
        Planning géré par votre coach. Les matchs sont signalés en rose, les entraînements en cyan.
      </p>
    </section>
  );
}

// =============================================================================
// MINI CARTE — version inclinée pour le greeting strip, ouvre modal au clic
// =============================================================================
export function LicenseeMiniCard({ className = "", style = {} }) {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/sport/licensee/me", customerAuthHeaders())
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!me) return null;
  const fullName = `${me.first_name || ""} ${me.last_name || ""}`.trim() || "Licencié";

  return (
    <>
      <motion.button type="button" onClick={() => setOpen(true)}
        animate={{ rotateY: [-3, 3, -3], rotateX: [1, -1, 1] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className={`group relative pointer-events-auto ${className}`}
        style={{ transform: "rotate(-8deg)", transformOrigin: "center", transformStyle: "preserve-3d", perspective: "800px", ...style }}
        aria-label="Voir ma carte de licence"
        data-testid="licensee-mini-card">
        <div className="relative rounded-xl overflow-hidden shadow-2xl group-hover:shadow-pink-500/40 transition-shadow"
          style={{
            background: `linear-gradient(160deg, ${PRIMARY} 0%, #4a1f40 50%, #1a0a18 100%)`,
            aspectRatio: "1 / 1.586",
            border: "2px solid #e9c878",
            width: "100%",
          }}>
          {/* Hologramme animé */}
          <div aria-hidden className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              background: "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.18) 48%, rgba(233,200,120,0.32) 50%, rgba(255,255,255,0.18) 52%, transparent 65%)",
              backgroundSize: "200% 100%",
              animation: "iphbHoloShimmer 3.6s linear infinite",
            }}/>
          {/* Logo club central en filigrane */}
          {me.club_logo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <img src={me.club_logo} alt="" className="w-[70%] opacity-[0.12]"/>
            </div>
          )}
          {/* Bandeau haut : logo club + LOGO FFHB en miniature */}
          <div className="absolute top-0 left-0 right-0 px-2 pt-1.5 pb-1 flex items-center justify-between border-b z-10"
            style={{ borderColor: "rgba(233,200,120,0.25)" }}>
            {me.club_logo && (
              <img src={me.club_logo} alt="" className="h-5 w-5 object-contain"/>
            )}
            <img src="/uploads/logo-ffhb-real.png" alt="FFHB" className="h-5 w-5 object-contain"
              onError={(e) => { e.currentTarget.src = "/uploads/logo-ffhb.svg"; }}/>
          </div>

          {/* Photo + nom centrés (avatar ROND) */}
          <div className="relative h-full flex flex-col items-center justify-center pt-4 px-2 pb-2 z-10">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 shadow-lg flex-shrink-0"
              style={{ borderColor: "#e9c878", background: "rgba(255,255,255,0.05)" }}>
              {me.license_photo ? (
                <img src={me.license_photo} alt={fullName} className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-black text-base">
                  {(me.first_name?.[0] || "?").toUpperCase()}
                </div>
              )}
            </div>
            <div className="text-white font-black uppercase text-center mt-1.5 line-clamp-1 max-w-full"
              style={{
                fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif",
                fontSize: "0.7rem",
                letterSpacing: "0.04em",
                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
              }}>
              {fullName}
            </div>
            <div className="text-[7px] uppercase tracking-widest font-black mt-0.5"
              style={{ color: "#e9c878" }}>
              N° {me.license_number || "—"}
            </div>
            <div className="text-[7px] uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded-full"
              style={{ background: "rgba(233,200,120,0.2)", color: "#e9c878", border: "1px solid rgba(233,200,120,0.4)" }}>
              {me.license_category || "Licencié"}
            </div>
          </div>

          {/* Icône loupe en overlay (signal cliquable) */}
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-transform z-20"
            style={{ background: SECONDARY, border: "1.5px solid white" }}
            data-testid="license-zoom-icon">
            <Search size={13} className="text-white"/>
          </div>

          {/* Mention au clic */}
          <div className="absolute bottom-1 left-0 right-0 text-center text-[7px] uppercase tracking-widest font-black opacity-80"
            style={{ color: "#e9c878" }}>
            Cliquer pour agrandir
          </div>
        </div>
      </motion.button>

      {/* MODAL plein écran : carte droite, en grand — porté vers document.body pour éviter
          le stacking context du wrapper parent (qui a un transform). */}
      {open && createPortal((
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="license-card-modal">
          <div className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="absolute -top-12 right-0 text-white/80 hover:text-white text-sm font-black uppercase tracking-widest"
              data-testid="close-license-card">
              ✕ Fermer
            </button>
            <DigitalLicenseCard data={me}/>
          </div>
        </div>
      ), document.body)}
    </>
  );
}

// =============================================================================
// LICENSEE OFFERS DIRECTORY — Annuaire CE style (recherche + filtres secteurs)
// =============================================================================
function LicenseeOffersDirectory({ offers }) {
  const [query, setQuery] = useState("");
  const [activeSector, setActiveSector] = useState("all");

  // Compter les offres par secteur (basé sur partner.sector)
  const sectors = React.useMemo(() => {
    const counts = {};
    for (const o of offers) {
      const s = (o.partner?.sector || "").trim() || "Autres";
      counts[s] = (counts[s] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
  }, [offers]);

  // Filtrage live
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return offers.filter((o) => {
      const sec = (o.partner?.sector || "").trim() || "Autres";
      if (activeSector !== "all" && sec !== activeSector) return false;
      if (!q) return true;
      const hay = [
        o.title,
        o.description,
        o.discount_label,
        o.partner?.name,
        o.partner?.sector,
        o.partner?.address,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [offers, query, activeSector]);

  return (
    <section data-testid="licensee-offers-directory">
      {/* En-tête + bandeau "annuaire CE" */}
      <div className="rounded-2xl overflow-hidden mb-4 shadow-md"
        style={{ background: `linear-gradient(135deg, ${PRIMARY}, #4F1B7C)` }}>
        <div className="px-5 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-pink-300 font-black tracking-[0.25em] text-[10px] uppercase">
              Avantages licenciés · Annuaire partenaires
            </div>
            <h2 className="font-black uppercase italic text-2xl sm:text-3xl text-white mt-1"
              style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
              Vos <span style={{ color: SECONDARY }}>offres</span> exclusives
            </h2>
            <p className="text-xs text-white/70 mt-1 max-w-xl leading-relaxed">
              Réductions et bons plans réservés aux licenciés IPHB chez nos partenaires locaux —
              recherchez par enseigne, ville ou type de prestation.
            </p>
          </div>
          {/* Compteur */}
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 border border-white/20 self-start sm:self-end backdrop-blur">
            <Tag size={13} className="text-pink-300"/>
            <span className="text-xs font-black uppercase tracking-widest text-white">
              {offers.length} offre{offers.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="px-5 sm:px-6 pb-4">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un partenaire, un commerce, un avantage…"
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-white/20 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-400 transition shadow-inner"
              data-testid="licensee-offers-search"
            />
          </div>
        </div>
      </div>

      {/* Filtres par secteur */}
      {sectors.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="licensee-offers-filters">
          <button
            type="button"
            onClick={() => setActiveSector("all")}
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition border ${
              activeSector === "all"
                ? "text-white border-transparent shadow-md"
                : "bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600"
            }`}
            style={activeSector === "all" ? { background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` } : {}}
            data-testid="licensee-filter-all"
          >
            Toutes ({offers.length})
          </button>
          {sectors.map(({ key, count }) => {
            const active = activeSector === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSector(active ? "all" : key)}
                className={`px-3.5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition border ${
                  active
                    ? "text-white border-transparent shadow-md"
                    : "bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600"
                }`}
                style={active ? { background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` } : {}}
                data-testid={`licensee-filter-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`}
              >
                {key} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Résultats */}
      {filtered.length === 0 ? (
        <div className="rounded-xl bg-slate-50 p-8 text-center text-slate-500 text-sm border border-dashed border-slate-300"
          data-testid="licensee-offers-empty">
          <Search size={28} className="mx-auto mb-2 opacity-30"/>
          <div className="font-bold text-slate-700">Aucune offre ne correspond à votre recherche</div>
          <div className="text-[12px] mt-1">
            Essayez un autre mot-clé ou retirez les filtres pour voir toutes les offres disponibles.
          </div>
        </div>
      ) : (
        <>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-black mb-3">
            {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
            {(query || activeSector !== "all") && (
              <button
                type="button"
                onClick={() => { setQuery(""); setActiveSector("all"); }}
                className="ml-3 text-pink-600 hover:text-pink-700 normal-case tracking-normal underline font-bold"
                data-testid="licensee-offers-reset"
              >
                Réinitialiser
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((o) => (
              <LicenseeOfferCard key={o.id} offer={o}/>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// =============================================================================
// LICENSEE DASHBOARD — section principale espace licencié
// =============================================================================
export default function LicenseeDashboard({ hideOffers = false }) {
  const [me, setMe] = useState(null);
  const [offers, setOffers] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/sport/licensee/me", customerAuthHeaders()).then((r) => r.data).catch((err) => { console.error("[LicenseeDashboard] /me", err?.response?.status, err?.response?.data); return null; }),
      hideOffers ? Promise.resolve([]) : api.get("/sport/licensee/offers").then((r) => r.data || []).catch(() => []),
      api.get("/sport/licensee/schedule", customerAuthHeaders()).then((r) => r.data).catch(() => null),
    ]).then(([m, o, s]) => {
      if (cancelled) return;
      setMe(m);
      setOffers(o || []);
      setSchedule(s);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hideOffers]);

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500 text-sm uppercase tracking-widest">Chargement de votre espace licencié…</div>
    );
  }

  return (
    <div className="space-y-8" data-testid="licensee-dashboard">

      {/* PLANNING DE LA SEMAINE */}
      <ScheduleSection schedule={schedule}/>

      {/* OFFRES PRÉFÉRENTIELLES — annuaire CE-style (masqué si le module DigiPilote est désactivé) */}
      {!hideOffers && (
        offers.length === 0 ? (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={18} style={{ color: SECONDARY }}/>
              <h2 className="font-black uppercase tracking-widest text-sm" style={{ color: PRIMARY }}>
                Offres préférentielles partenaires
              </h2>
            </div>
            <div className="rounded-xl bg-slate-50 p-8 text-center text-slate-500 text-sm">
              <Tag size={28} className="mx-auto mb-2 opacity-30"/>
              Aucune offre disponible pour le moment. Revenez bientôt — le club ajoute régulièrement de nouveaux avantages !
            </div>
          </section>
        ) : (
          <LicenseeOffersDirectory offers={offers}/>
        )
      )}
    </div>
  );
}
